import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useContext, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TimeInput from './TimeInput'

import FakeInput from 'components/Common/Form/Input/FakeInput'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import Group from 'components/utils/Group'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import ModalContext from 'contexts/ModalContext'
import MobileSelectDatesSelfClose from 'tripPlanner/components/TripModal/Common/MobileSelectDatesSelfClose'

const OverlappingGroup = styled(Group)`
  * + * {
    margin-inline-start: -1px;
  }
`

const DateInput = styled(FakeInput)`
  &.date-wrap {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    min-width: ${rem(160)};
    height: ${rem(48)};

    &:not(.error):not(.disabled):focus-within {
      z-index: 1;
    }
  }
`

function MobileAccommodationDateTimeFields() {
  const intl = useIntl()
  const { watch, setValue, register } = useFormContext()
  const startDate = watch('startDate')
  const endDate = watch('endDate')
  const dateDropdownTrigger = useRef<HTMLDivElement | null>(null)

  const onDatesChange = useCallback(
    (dates: { startDate: moment.Moment, endDate?: moment.Moment | null }) => {
      setValue('startDate', dates.startDate.format(ISO_DATE_FORMAT), {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('endDate', dates.endDate?.format(ISO_DATE_FORMAT), {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue],
  )

  const showModal = useContext(ModalContext)
  const startDateMoment = startDate ?
      moment(startDate, ISO_DATE_FORMAT) :
    undefined
  const endDateMoment = endDate ? moment(endDate, ISO_DATE_FORMAT) : undefined

  const onClearDates = useCallback(() => {
    setValue('startDate', undefined)
    setValue('endDate', undefined)
  }, [setValue])

  const selectDatesTitle = intl.formatMessage({
    id: 'HaAdCq0uHpMfTeGQGunW',
    defaultMessage: 'Select dates',
    description: 'Title for mobile date selection modal in accommodation form',
  })

  const checkInLabel = intl.formatMessage({
    id: 'doetjnRKsONOKwisSBa2',
    defaultMessage: 'Check-in',
    description: 'Label for check-in date field in accommodation form',
  })

  const checkOutLabel = intl.formatMessage({
    id: 'YjA2wuomDpmyyilrGEJb',
    defaultMessage: 'Check-out',
    description: 'Label for check-out date field in accommodation form',
  })

  const selectDatePlaceholder = intl.formatMessage({
    id: 'T3rFphdp45AaVqFJxCKt',
    defaultMessage: 'Select date',
    description: 'Placeholder text for date picker in accommodation form',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: '9hvkRZbaw6cfcdig0gQD',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in accommodation form',
  })

  const timeLabel = intl.formatMessage({
    id: '9i7qqiIXnVlMesXW-52M',
    defaultMessage: 'Time',
    description: 'Form input label for the time field in the accommodation form',
  })

  const onClickDateInput = useCallback(() => {
    showModal(
      <ModalBase height="full">
        <MobileSelectDatesSelfClose
          title={selectDatesTitle}
          doneLabel="Apply"
          initialStartDate={startDateMoment}
          initialEndDate={endDateMoment}
          onChange={(startDate, endDate) => {
            onDatesChange({ startDate, endDate })
          }}
          onRemove={onClearDates}
          showTripDates
          startLabel={checkInLabel}
          endLabel={checkOutLabel}
        />
      </ModalBase>,
    )
  }, [checkInLabel, checkOutLabel, endDateMoment, onClearDates, onDatesChange, selectDatesTitle, showModal, startDateMoment])

  return (
    <Group direction="vertical" gap={16} ref={dateDropdownTrigger}>
      <OverlappingGroup
        direction="horizontal"
        horizontalAlign="stretch"
        verticalAlign="end"
      >
        <DateInput
          label={checkInLabel}
          value={startDate && moment(startDate).format(DMY_CASUAL_SHORT_FORMAT)}
          placeholder={selectDatePlaceholder}
          onClick={onClickDateInput}
          startIcon={<LineCalendarIcon />}
          data-testid="check-in-date"
          inputWrapClassName="date-wrap"
        />
        <TimeInput
          {...register('startTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
          required
          defaultValue="15:00"
          hasIcon={false}
          showClear={false}
        />
      </OverlappingGroup>
      <OverlappingGroup
        direction="horizontal"
        horizontalAlign="stretch"
        verticalAlign="end"
      >
        <DateInput
          label={checkOutLabel}
          value={endDate && moment(endDate).format(DMY_CASUAL_SHORT_FORMAT)}
          placeholder={selectDatePlaceholder}
          onClick={onClickDateInput}
          startIcon={<LineCalendarIcon />}
          data-testid="check-out-date"
          inputWrapClassName="date-wrap"
        />
        <TimeInput
          {...register('endTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
          required
          defaultValue="10:00"
          hasIcon={false}
          showClear={false}
        />
      </OverlappingGroup>
    </Group>
  )
}

export default MobileAccommodationDateTimeFields
